// src/components/random-car/random-car.ts
// Random Car Generator tool page. All client-side: picks cars from the static
// curated dataset, respecting the optional budget/era/category/country filters.
// Cards are built with DOM APIs (never innerHTML with data) so no dataset or
// filter value is ever injected as markup.
import "./random-car.css";
import { CARS, type CarCategory, type CarEntry } from "../../data/cars";
import { buildAmazonSearchUrl, AFFILIATE_LINKS_ENABLED } from "../../utils/amazon-affiliate";

const CATEGORY_LABELS: Record<CarCategory, string> = {
  hypercar: "Hypercar",
  supercar: "Supercar",
  sports: "Sports car",
  muscle: "Muscle car",
  jdm: "JDM",
  classic: "Classic",
  luxury: "Luxury",
  suv: "SUV",
  "hot-hatch": "Hot hatch",
  ev: "EV",
  truck: "Truck",
  offroad: "Off-roader",
};

const usdFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const GARAGE_SIZE = 5;

let lastCarKey: string | null = null;
let spinCount = 0;

function carKey(car: CarEntry): string {
  return `${car.make} ${car.model}`;
}

/** Fill the category/country selects from what's actually in the dataset. */
function populateFilterOptions(categorySelect: HTMLSelectElement, countrySelect: HTMLSelectElement): void {
  const categories = [...new Set(CARS.map((car) => car.category))].sort((a, b) =>
    CATEGORY_LABELS[a].localeCompare(CATEGORY_LABELS[b]),
  );
  categories.forEach((category) => {
    categorySelect.append(new Option(CATEGORY_LABELS[category], category));
  });

  const countries = [...new Set(CARS.map((car) => car.country))].sort((a, b) => a.localeCompare(b));
  countries.forEach((country) => {
    countrySelect.append(new Option(country, country));
  });
}

function matchesBudget(car: CarEntry, budget: string): boolean {
  switch (budget) {
    case "u30":
      return car.priceUsd < 30_000;
    case "u100":
      return car.priceUsd < 100_000;
    case "u500":
      return car.priceUsd < 500_000;
    case "m1":
      return car.priceUsd >= 1_000_000;
    default:
      return true;
  }
}

interface FilterSelects {
  budget: HTMLSelectElement;
  era: HTMLSelectElement;
  category: HTMLSelectElement;
  country: HTMLSelectElement;
}

function filteredCars(filters: FilterSelects): CarEntry[] {
  const era = filters.era.value;
  const category = filters.category.value;
  const country = filters.country.value;
  const budget = filters.budget.value;

  return CARS.filter(
    (car) =>
      matchesBudget(car, budget) &&
      (era === "any" || car.era === era) &&
      (category === "any" || car.category === category) &&
      (country === "any" || car.country === country),
  );
}

/** Fisher-Yates shuffle (copy), used for the 5-car garage. */
function shuffled<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Pick a random car, never the same one twice in a row (when avoidable). */
function pickRandomCar(pool: CarEntry[]): CarEntry {
  const candidates = pool.length > 1 ? pool.filter((car) => carKey(car) !== lastCarKey) : pool;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

function buildCard(car: CarEntry): HTMLElement {
  const card = document.createElement("article");
  card.className = "rcg-card";

  const badge = document.createElement("span");
  badge.className = "rcg-card__badge";
  badge.textContent = CATEGORY_LABELS[car.category];

  const title = document.createElement("h3");
  title.className = "rcg-card__title";
  title.textContent = `${car.make} ${car.model}`;

  const meta = document.createElement("p");
  meta.className = "rcg-card__meta";
  meta.textContent = `${car.years} · ${car.country}`;

  const stats = document.createElement("dl");
  stats.className = "rcg-card__stats";
  const statPairs: Array<[string, string]> = [
    ["Price today", usdFormat.format(car.priceUsd)],
    ["Power", `${car.hp} hp`],
    ["0-60 mph", `${car.zeroToSixty.toFixed(1)} s`],
  ];
  statPairs.forEach(([label, value]) => {
    const stat = document.createElement("div");
    const dt = document.createElement("dt");
    dt.textContent = label;
    const dd = document.createElement("dd");
    dd.textContent = value;
    stat.append(dt, dd);
    stats.append(stat);
  });

  const fact = document.createElement("p");
  fact.className = "rcg-card__fact";
  fact.textContent = car.funFact;

  const actions = document.createElement("div");
  actions.className = "rcg-card__actions";

  const photosLink = document.createElement("a");
  photosLink.className = "rcg-card__action";
  photosLink.href = `/?q=${encodeURIComponent(carKey(car))}`;
  photosLink.textContent = "See real photos";

  actions.append(photosLink);

  if (AFFILIATE_LINKS_ENABLED) {
    const shopLink = document.createElement("a");
    shopLink.className = "rcg-card__action rcg-card__action--shop";
    shopLink.href = buildAmazonSearchUrl(carKey(car));
    shopLink.target = "_blank";
    shopLink.rel = "sponsored noopener";
    shopLink.textContent = "Shop the model";
    actions.append(shopLink);
  }

  card.append(badge, title, meta, stats, fact, actions);
  return card;
}

function renderNoMatches(result: HTMLElement): void {
  result.replaceChildren();
  const empty = document.createElement("p");
  empty.className = "rcg-empty";
  empty.textContent = "No cars match those filters. Loosen one (that $1M+ hot hatch doesn't exist yet) and spin again.";
  result.append(empty);
}

function spinLabel(count: number): string {
  if (count >= 50) return `Spin #${count}. Certified car enthusiast.`;
  if (count >= 25) return `Spin #${count}. You really can't stop, huh?`;
  if (count >= 10) return `Spin #${count}. The one is out there.`;
  return `Spin #${count}`;
}

function countSpin(counter: HTMLElement): void {
  spinCount++;
  counter.hidden = false;
  counter.textContent = spinLabel(spinCount);
}

function generateOne(filters: FilterSelects, result: HTMLElement, counter: HTMLElement): void {
  const pool = filteredCars(filters);
  if (pool.length === 0) {
    renderNoMatches(result);
    return;
  }

  const car = pickRandomCar(pool);
  lastCarKey = carKey(car);
  result.replaceChildren(buildCard(car));
  countSpin(counter);
}

function generateGarage(filters: FilterSelects, result: HTMLElement, counter: HTMLElement): void {
  const pool = filteredCars(filters);
  if (pool.length === 0) {
    renderNoMatches(result);
    return;
  }

  const picks = shuffled(pool).slice(0, GARAGE_SIZE);
  lastCarKey = carKey(picks[picks.length - 1]);

  result.replaceChildren();

  const heading = document.createElement("h2");
  heading.className = "rcg-garage-heading";
  heading.textContent = picks.length === GARAGE_SIZE ? "Your 5-car dream garage" : "Your dream garage";
  result.append(heading);

  if (picks.length < GARAGE_SIZE) {
    const note = document.createElement("p");
    note.className = "rcg-garage-note";
    note.textContent = `Only ${picks.length} ${picks.length === 1 ? "car matches" : "cars match"} those filters, so here's every one of them.`;
    result.append(note);
  }

  const list = document.createElement("div");
  list.className = "rcg-garage-list";
  picks.forEach((car, index) => {
    const card = buildCard(car);
    card.style.animationDelay = `${index * 0.12}s`;
    list.append(card);
  });
  result.append(list);

  const totalUsd = picks.reduce((sum, car) => sum + car.priceUsd, 0);
  const total = document.createElement("p");
  total.className = "rcg-garage-total";
  const totalLabel = document.createElement("span");
  totalLabel.textContent = "Garage total: ";
  const totalValue = document.createElement("strong");
  totalValue.textContent = usdFormat.format(totalUsd);
  total.append(totalLabel, totalValue);
  result.append(total);

  countSpin(counter);
}

function initializeRandomCarGenerator(): void {
  const generateButton = document.getElementById("rcg-generate");
  const garageButton = document.getElementById("rcg-garage");
  const result = document.getElementById("rcg-result");
  const counter = document.getElementById("rcg-spin-count");
  const budget = document.getElementById("rcg-budget") as HTMLSelectElement | null;
  const era = document.getElementById("rcg-era") as HTMLSelectElement | null;
  const category = document.getElementById("rcg-category") as HTMLSelectElement | null;
  const country = document.getElementById("rcg-country") as HTMLSelectElement | null;

  if (!generateButton || !garageButton || !result || !counter || !budget || !era || !category || !country) return;

  const filters: FilterSelects = { budget, era, category, country };
  populateFilterOptions(category, country);

  generateButton.addEventListener("click", () => generateOne(filters, result, counter));
  garageButton.addEventListener("click", () => generateGarage(filters, result, counter));
}

document.addEventListener("DOMContentLoaded", initializeRandomCarGenerator);
